import { useCallback } from 'react';
import { css } from '@emotion/react';
import {
  ConnectDevice,
  DialpadIconButton,
  DialpadPopout,
  PairDeviceModal,
  useDialpadDialer,
  useDialpadManagerClient,
  useDialpadWidgetControl,
} from '@frontend/dialpad';
import { useConditionalChannelSubscription } from '@frontend/queue';
import { theme } from '@frontend/theme';

export const DialpadWrap = () => {
  const isConnected = useDialpadManagerClient((ctx) => ctx.isDeskphoneConnected);
  const availableDevices = useDialpadManagerClient((ctx) => ctx.availableDevices);
  const hasAvailableDevices = !!availableDevices?.length;
  return (
    <>
      <Wrapper styled={true}>
        <DialpadIconButton />
        {isConnected && <DialpadPopout />}
        {!isConnected && !hasAvailableDevices && <PairDeviceModal />}
        {!isConnected && hasAvailableDevices && <ConnectDevice />}
      </Wrapper>
    </>
  );
};

const Wrapper = ({ styled, children }: { styled: boolean; children: React.ReactNode }) => {
  const isConnected = useDialpadManagerClient((ctx) => ctx.isDeskphoneConnected);

  const setDialpadDialer = useDialpadDialer((ctx) => ctx.setCurrentDial);
  const setDialpadOutboundNumber = useDialpadManagerClient((ctx) => ctx.setCurrentOutboundNumber);
  const currentDialpadOutboundNumber = useDialpadManagerClient((ctx) => ctx.currentOutboundPhoneNumber);
  const openDialpad = useDialpadWidgetControl((ctx) => ctx.open);
  const outboundNumbers = useDialpadManagerClient((ctx) => ctx.availablePhoneNumbers);

  const handler = useCallback(
    (event: { type: string; payload: { dialNumber: string; outboundNumber?: string } }) => {
      if (event.type === 'place-call' && isConnected) {
        const { dialNumber, outboundNumber: specificOutboundNumber } = event.payload;
        const outboundNumber = specificOutboundNumber
          ? outboundNumbers.find((item) => item.number === specificOutboundNumber)
          : currentDialpadOutboundNumber;
        setDialpadDialer(dialNumber);
        setDialpadOutboundNumber(outboundNumber);
        openDialpad?.();

        return true;
      }

      return false;
    },
    [
      isConnected,
      outboundNumbers,
      currentDialpadOutboundNumber,
      setDialpadDialer,
      setDialpadOutboundNumber,
      openDialpad,
    ]
  );

  useConditionalChannelSubscription(
    'dialpad',
    useCallback(
      (event) => {
        if (event.type === 'place-call') {
          handler(event);
          return true;
        }
        return false;
      },
      [handler]
    ),
    isConnected // Only subscribe when connected
  );

  if (!styled) {
    return (
      <div
        css={css`
          position: relative;
        `}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      css={css`
        height: 100%;
        display: flex;
        align-items: center;
        position: relative;
        padding: ${theme.spacing(0.5)};
      `}
    >
      {children}
    </div>
  );
};
